/**
 * The three activity types added for the rewritten Module 1:
 *   order  - put the steps of a process into sequence
 *   rate   - judge how risky an everyday situation is
 *   design - the practical task, a saved sticker pack
 *
 * The older activity renderers live in ./pages.jsx.
 */
import { useMemo, useState, useEffect } from 'react';
import CubMascot from '../components/CubMascot.jsx';

function shuffle(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/* ============================================================
   order
   ============================================================ */
export function OrderPage({ page, onComplete }) {
  const pool = useMemo(() => shuffle(page.steps), [page.steps]);
  const [placed, setPlaced] = useState([]);
  const [wrong, setWrong] = useState(null);

  const done = placed.length === page.steps.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const choose = (step) => {
    // Correct only if this is the very next step in the real sequence.
    if (page.steps[placed.length].id === step.id) {
      setPlaced((p) => [...p, step.id]);
      setWrong(null);
    } else {
      setWrong(step.id);
      setTimeout(() => setWrong(null), 500);
    }
  };

  const remaining = pool.filter((s) => !placed.includes(s.id));

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>

      <div className="sort-progress">
        Step {placed.length} of {page.steps.length} in place
      </div>

      <div className="order-track">
        {page.steps.map((_, i) => {
          const step = page.steps.find((s) => s.id === placed[i]);
          return (
            <div
              key={i}
              className={`order-slot ${step ? 'is-filled' : ''} ${i === placed.length ? 'is-next' : ''}`}
            >
              <span className="order-num">{i + 1}</span>
              {step ? (
                <span className="order-text anim-pop">
                  <span aria-hidden="true">{step.icon}</span> {step.text}
                </span>
              ) : (
                <span className="order-blank">
                  {i === placed.length ? 'What happens next?' : '…'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {remaining.length > 0 && (
        <div className="order-pool">
          {remaining.map((step) => (
            <button
              key={step.id}
              type="button"
              className={`sort-item ${wrong === step.id ? 'anim-shake is-wrong' : ''}`}
              onClick={() => choose(step)}
            >
              <span aria-hidden="true">{step.icon}</span>
              <span>{step.text}</span>
            </button>
          ))}
        </div>
      )}

      {done && (
        <div className="feedback good anim-pop">
          <span className="feedback-emoji" aria-hidden="true">🎉</span>
          <div>
            <strong>Perfect order!</strong>
            <p>{page.successText ?? 'You followed the whole journey from start to finish.'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   rate
   ============================================================ */
export function RatePage({ page, onComplete }) {
  const [index, setIndex] = useState(0);
  const [answered, setAnswered] = useState({});
  const [picked, setPicked] = useState(null);

  const current = page.situations[index];
  const isLast = index === page.situations.length - 1;
  const done = Object.keys(answered).length === page.situations.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const choose = (levelId) => {
    if (picked) return;
    setPicked(levelId);
    setAnswered((a) => ({ ...a, [current.id]: levelId }));
  };

  const next = () => {
    setPicked(null);
    setIndex((i) => Math.min(i + 1, page.situations.length - 1));
  };

  const correct = picked === current.answer;
  const correctLevel = page.levels.find((l) => l.id === current.answer);
  const rightCount = page.situations.filter((s) => answered[s.id] === s.answer).length;

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>

      <div className="sort-progress">
        Situation {index + 1} of {page.situations.length} · {rightCount} judged correctly
      </div>

      <div className="risk-card anim-pop" key={current.id}>
        <span className="risk-icon" aria-hidden="true">{current.icon}</span>
        <p className="risk-text">{current.text}</p>
      </div>

      <div className="risk-levels">
        {page.levels.map((level) => {
          const isPicked = picked === level.id;
          const isAnswer = picked && level.id === current.answer;
          return (
            <button
              key={level.id}
              type="button"
              className={`risk-level t-${level.color} ${isPicked ? 'is-picked' : ''} ${isAnswer ? 'is-answer' : ''}`}
              onClick={() => choose(level.id)}
              disabled={Boolean(picked)}
              aria-pressed={isPicked}
            >
              <span className="risk-level-icon" aria-hidden="true">{level.icon}</span>
              <span>{level.label}</span>
            </button>
          );
        })}
      </div>

      {picked && (
        <>
          <div className={`feedback ${correct ? 'good' : 'neutral'} anim-pop`}>
            <CubMascot
              avatar={correct ? 'cub-mint' : 'cub-sky'}
              mood={correct ? 'cheer' : 'think'}
              size={72}
            />
            <div>
              <strong>
                {correct
                  ? 'Spot on!'
                  : `Close — most people would call this ${correctLevel?.label.toLowerCase()}.`}
              </strong>
              <p>{current.why}</p>
            </div>
          </div>

          {!isLast && (
            <div className="row center mt-md">
              <button type="button" className="btn" onClick={next}>
                Next situation →
              </button>
            </div>
          )}
        </>
      )}

      {done && (
        <div className="feedback good anim-pop">
          <span className="feedback-emoji" aria-hidden="true">🎯</span>
          <div>
            <strong>Risk radar trained!</strong>
            <p>
              You judged {rightCount} out of {page.situations.length} correctly. Remember: risk is
              about how likely AND how bad — not about how scary something looks.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   design - the practical task
   ============================================================ */

/** One sticker, drawn the same way in the editor and in reports. */
export function Sticker({ sticker, size = 132 }) {
  return (
    <div
      className={`sticker shape-${sticker.shape} t-${sticker.color}`}
      style={{ width: size, height: size }}
    >
      <span className="sticker-icon" aria-hidden="true">{sticker.icon}</span>
      <span className="sticker-slogan">{sticker.slogan}</span>
    </div>
  );
}

const blankSticker = (page) => ({
  shape: page.shapes[0].id,
  color: page.colors[0],
  icon: page.icons[0],
  slogan: '',
});

export function DesignPage({ page, onComplete, onSave, saved }) {
  const required = page.requiredCount ?? 4;

  const [pack, setPack] = useState(() => saved ?? []);
  const [draft, setDraft] = useState(() => blankSticker(page));
  const [editingAt, setEditingAt] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const complete = pack.length >= required;

  useEffect(() => {
    if (complete) onComplete();
  }, [complete, onComplete]);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const commit = () => {
    if (!draft.slogan.trim()) return;
    const cleaned = { ...draft, slogan: draft.slogan.trim() };
    setPack((p) => {
      if (editingAt === null) return [...p, cleaned];
      const copy = [...p];
      copy[editingAt] = cleaned;
      return copy;
    });
    setDraft(blankSticker(page));
    setEditingAt(null);
    setStatus('');
  };

  const edit = (i) => {
    setDraft(pack[i]);
    setEditingAt(i);
  };

  const remove = (i) => {
    setPack((p) => p.filter((_, idx) => idx !== i));
    if (editingAt === i) {
      setDraft(blankSticker(page));
      setEditingAt(null);
    }
  };

  const save = async () => {
    setBusy(true);
    setStatus('');
    try {
      await onSave(pack);
      setStatus('saved');
    } catch (err) {
      setStatus(err.message);
    } finally {
      setBusy(false);
    }
  };

  const missing = Math.max(0, required - pack.length);

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>

      <div className="sort-progress">
        {pack.length} of {required} stickers designed
        {pack.length > required ? ' (bonus ones welcome!)' : ''}
      </div>

      {/* ---------- the pack so far ---------- */}
      <div className="sticker-pack">
        {pack.map((s, i) => (
          <div key={i} className="sticker-slot">
            <Sticker sticker={s} />
            <div className="sticker-actions">
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => edit(i)}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost danger"
                onClick={() => remove(i)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {Array.from({ length: missing }).map((_, i) => (
          <div key={`empty-${i}`} className="sticker-empty">
            <span className="sticker-plus" aria-hidden="true">＋</span>
            <span className="small">Sticker {pack.length + i + 1}</span>
          </div>
        ))}
      </div>

      {/* ---------- the editor ---------- */}
      <div className="designer">
        <div className="designer-preview">
          <Sticker
            sticker={{ ...draft, slogan: draft.slogan || 'Your message here' }}
            size={150}
          />
          <span className="small faint">
            {editingAt === null ? 'New sticker' : `Editing sticker ${editingAt + 1}`}
          </span>
        </div>

        <div className="designer-controls">
          <div className="field">
            <label>Shape</label>
            <div className="chip-row">
              {page.shapes.map((sh) => (
                <button
                  key={sh.id}
                  type="button"
                  className={`chip ${draft.shape === sh.id ? 'is-picked' : ''}`}
                  onClick={() => set('shape', sh.id)}
                  aria-pressed={draft.shape === sh.id}
                >
                  <span aria-hidden="true">{sh.icon}</span> {sh.label}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label>Colour</label>
            <div className="chip-row">
              {page.colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`swatch t-${c} ${draft.color === c ? 'is-picked' : ''}`}
                  onClick={() => set('color', c)}
                  aria-label={`Colour ${c}`}
                  aria-pressed={draft.color === c}
                />
              ))}
            </div>
          </div>

          <div className="field">
            <label>Picture</label>
            <div className="chip-row icon-row">
              {page.icons.map((ic) => (
                <button
                  key={ic}
                  type="button"
                  className={`icon-pick ${draft.icon === ic ? 'is-picked' : ''}`}
                  onClick={() => set('icon', ic)}
                  aria-label={`Icon ${ic}`}
                  aria-pressed={draft.icon === ic}
                >
                  {ic}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label htmlFor="slogan">Slogan</label>
            <input
              id="slogan"
              value={draft.slogan}
              onChange={(e) => set('slogan', e.target.value.slice(0, 60))}
              placeholder="Write your own, or tap one below"
              maxLength={60}
            />
            <div className="hint">{draft.slogan.length}/60 — short and punchy works best.</div>
            <div className="chip-row mt-md">
              {page.slogans.map((sl) => (
                <button
                  key={sl}
                  type="button"
                  className={`chip ${draft.slogan === sl ? 'is-picked' : ''}`}
                  onClick={() => set('slogan', sl)}
                >
                  {sl}
                </button>
              ))}
            </div>
          </div>

          <button type="button" className="btn" onClick={commit} disabled={!draft.slogan.trim()}>
            {editingAt === null ? '➕ Add to my pack' : '✓ Save changes'}
          </button>
        </div>
      </div>

      {/* ---------- save ---------- */}
      <div className="designer-save">
        {complete ? (
          <div className="feedback good">
            <span className="feedback-emoji" aria-hidden="true">🎨</span>
            <div>
              <strong>Your pack is ready!</strong>
              <p>{page.successText}</p>
            </div>
          </div>
        ) : (
          <p className="muted tc">
            Design {missing} more sticker{missing === 1 ? '' : 's'} to finish your pack.
          </p>
        )}

        <div className="row center gap-sm mt-md">
          <button
            type="button"
            className="btn btn-lg"
            onClick={save}
            disabled={!pack.length || busy}
          >
            {busy ? 'Saving…' : '💾 Save my sticker pack'}
          </button>
        </div>

        {status === 'saved' && (
          <p className="tc mt-md save-ok">✓ Saved! Your teacher can see your pack now.</p>
        )}
        {status && status !== 'saved' && <p className="tc mt-md save-bad">{status}</p>}
      </div>
    </div>
  );
}
